'use client';

import { useEffect, useRef } from 'react';
import { useUserLogged } from '@/hooks/useUserLogged';
import dynamic from 'next/dynamic';
import TrialSeatGate from '@/components/TrialAccess/TrialSeatGate';
import { MainLayout } from '@/layouts/DashboardLayout/MainContent/main-layout';
import { redirect } from 'next/navigation';
import { isKnowledgeBaseDevBypassPath } from '@/lib/knowledgeBase/access';
import { usePathname } from 'next/navigation';
import type { NavigationConfigItem } from '@/lib/navigationConfig.types';

// Deferred deliberately. BackfillStatusTracker renders null on every page
// load except the rare one where a backfill is actively running/completed/
// errored (see its own early-return guard), yet it and the team-insights
// query-key hook it pulls in for cache invalidation were shipped in every
// dashboard route's initial bundle.
const BackfillStatusTracker = dynamic(
  () => import('@/components/IngestionStatusTracker/BackfillStatusTracker')
);

const SESSION_RELOAD_KEY = 'ent_session_reload_once';

// True when this tab may reload once to recover its session. False if it
// already did (the key is consumed) or storage is unavailable, since without
// the key a reload could not be bounded.
function claimSessionReload(): boolean {
  try {
    if (window.sessionStorage.getItem(SESSION_RELOAD_KEY)) {
      window.sessionStorage.removeItem(SESSION_RELOAD_KEY);
      return false;
    }
    window.sessionStorage.setItem(SESSION_RELOAD_KEY, '1');
    return true;
  } catch {
    return false;
  }
}

function clearSessionReload(): void {
  try {
    window.sessionStorage.removeItem(SESSION_RELOAD_KEY);
  } catch {
    // Storage unavailable: there is no key to clear.
  }
}

type SessionCheck = 'live' | 'none' | 'unknown';

// Asks the session route directly rather than through getSession(), which
// resolves null on a failed fetch too (next-auth client/_utils.js fetchData)
// and so cannot tell "signed out" from "could not ask". Same route, same
// side effects.
async function checkServerSession(): Promise<SessionCheck> {
  try {
    const res = await fetch('/api/auth/session');
    if (!res.ok) return 'unknown';
    const body: unknown = await res.json();
    return body && typeof body === 'object' && Object.keys(body).length > 0
      ? 'live'
      : 'none';
  } catch {
    return 'unknown';
  }
}

interface DashboardLayoutClientProps {
  children: React.ReactNode;
  navConfig: NavigationConfigItem[];
}

export default function DashboardLayoutClient({
  children,
  navConfig,
}: DashboardLayoutClientProps) {
  const { status, user, organization, orgResolutionDone } = useUserLogged();
  const pathname = usePathname();
  const sessionLost =
    status === 'unauthenticated' &&
    !pathname?.startsWith('/documentation/') &&
    !isKnowledgeBaseDevBypassPath(pathname);
  const hasUser = !!user?.UserUUID;
  // Only a page with nothing to show waits on the check; a cached user keeps
  // rendering while it runs.
  const waitingForSession = sessionLost && !hasUser;
  const sessionChecked = useRef(false);

  // The server gate admitted this page, so 'unauthenticated' is a session
  // that ended after load (e.g. signed out in another tab). Once its session
  // is null, SessionProvider never refetches on its own, so ask the server
  // once: no session goes to /login. With no user, a live session reloads and
  // the server re-seeds SessionProvider from the cookie; the reload is
  // one-shot per tab, so if the server still seeds no session (a cookie it
  // cannot decode) the next attempt goes to /login instead of reloading
  // forever, and an unanswered check goes to /login since there is no page to
  // keep. With a cached user, a live or unanswered check keeps the page: a
  // focus refetch that failed offline (laptop wake) also reads as
  // 'unauthenticated', and leaving then would strand the tab on an error page.
  useEffect(() => {
    if (!sessionLost || sessionChecked.current) return;
    sessionChecked.current = true;
    void checkServerSession().then((session) => {
      if (session === 'none') window.location.replace('/login');
      else if (hasUser) return;
      else if (session === 'live' && claimSessionReload())
        window.location.reload();
      else window.location.replace('/login');
    });
  }, [sessionLost, hasUser]);

  const sessionReady = status === 'authenticated' || !!user?.UserUUID;
  useEffect(() => {
    if (sessionReady) clearSessionReload();
  }, [sessionReady]);

  // Dashboard routes require a workspace. A logged-in user whose org
  // resolution finished with nothing (no OrganizationUUID and no legacy
  // OrgName fallback) never completed workspace setup, so send them back to
  // onboarding to resume. orgResolutionDone stays false while the org is
  // still loading and on fetch errors, so a slow or down backend never
  // triggers this.
  //
  // Test the identifying fields, not the object. A user who never had a
  // workspace leaves the org store at its `emptyOrganization` default
  // ({ OrgUUID: '', OrgName: '', ApiKey: '' }), which UserContext copies into
  // context verbatim -- a truthy object, so `!organization` missed the most
  // common no-workspace case and let those users into dashboard routes with an
  // empty OrgUUID. Only an org resolved to literal null was caught before.
  //
  // Also honor user.OrganizationUUID: invite join sets that on the user row
  // before the org store has hydrated (or when setOrganizationByUUID fails
  // soft and leaves emptyOrganization). Bouncing those users to /onboarding
  // forces create-workspace on people who already joined via invite.
  if (
    status === 'authenticated' &&
    user?.UserUUID &&
    orgResolutionDone &&
    !user.OrganizationUUID &&
    !organization?.OrgUUID &&
    !organization?.OrgName &&
    !pathname?.startsWith('/documentation/')
  ) {
    redirect('/onboarding');
  }

  // Says what it is actually waiting on, not "Loading...". This gate is held
  // open by `useSession()`, and until it clears `useOrgUuid()` returns '' so
  // every org-gated query on the page stays undispatched -- a stall here looks
  // from the outside like a page that never issues a request. It also rendered
  // the same ten characters as the root Suspense fallback in `app/layout.tsx`,
  // which is indistinguishable in `document.body.innerText` and is why a
  // minutes-long stall was twice attributed to that boundary instead of here.
  if ((status === 'loading' && !user?.UserUUID) || waitingForSession) {
    return (
      <div
        className="flex min-h-screen items-center justify-center bg-ds-surface-white"
        role="status"
        aria-live="polite"
        aria-busy="true"
        data-loading-state="dashboard-session"
      >
        <p className="text-sm text-ds-text-subtle">Signing you in&hellip;</p>
      </div>
    );
  }

  return (
    <>
      <BackfillStatusTracker />
      <TrialSeatGate />
      <MainLayout navConfig={navConfig}>
        {children}
      </MainLayout>
    </>
  );
}
